/* Everything the whole page shares: who is signed in, the two filters, the view, and the
 * theme. The filters are global rather than per-card, so two panels cannot disagree about
 * what "now" means.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { write } from '../api/client';
import type { Me } from '../api/types';
import { rememberSession } from '../lib/boot';
import { labelFor } from '../lib/systems';

const THEME_KEY = 'analytics-theme';

export type Theme = 'light' | 'dark';

interface AppValue {
  me: Me;
  /** Days back, as the API's `days` parameter. */
  days: number;
  setDays: (days: number) => void;
  /** A system key, or `all`. */
  system: string;
  setSystem: (system: string) => void;
  /** True for the page written for whoever runs a system rather than the service — which
   *  is exactly the accounts that are not superadmins. Not a setting. */
  simple: boolean;
  /** One string in two registers: `word('Escalation rate', 'Sent to a person')`. */
  word: (full: string, plain: string) => string;
  /** What to call a system key. The session carries the names, so a system registered after
   *  this build shipped is called what it was registered as rather than by its folder name. */
  systemLabel: (key: string) => string;
  theme: Theme;
  toggleTheme: () => void;
  /** A request in flight over an answer already on screen — a filter change or a write, not
   *  a first load. Only this case is shown, by dimming what is being held back: an empty
   *  page has nothing to hold back, and saying so is a picture that appears and leaves. */
  refetching: boolean;
  track: (delta: number) => void;
  /** Bumped to re-ask every mounted query — used after a write lands. */
  nonce: number;
  reload: () => void;
  signOut: () => Promise<void>;
}

const AppContext = createContext<AppValue | null>(null);

function readTheme(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export function AppProvider({ me, children }: { me: Me; children: ReactNode }) {
  const [days, setDays] = useState(30);
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [busyCount, setBusyCount] = useState(0);
  const [nonce, setNonce] = useState(0);

  // The view follows the role and nothing else: an admin gets the page written for running
  // one system, a superadmin the instrument panel.
  const simple = !me.is_superadmin;

  // An admin confined to a single system has nothing to pick between, so the initial value
  // is that system rather than "all".
  const [system, setSystem] = useState(() =>
    !me.all_systems && me.systems.length < 2 ? (me.systems[0] ?? 'all') : 'all',
  );

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        /* Cookies blocked entirely. The theme still applies for this page. */
      }
      return next;
    });
  }, []);

  // A change made while the page is open has to reach here and not only the attribute: the
  // charts read their colours at draw time and are rebuilt by this state changing.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (e: MediaQueryListEvent) => {
      try {
        if (localStorage.getItem(THEME_KEY)) return;
      } catch {
        /* Storage unreadable: treat it as nothing pinned. */
      }
      const next: Theme = e.matches ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', next);
      setTheme(next);
    };
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  const track = useCallback((delta: number) => setBusyCount((n) => Math.max(0, n + delta)), []);

  // True once a first round of requests has come back — i.e. once there is something on the
  // page for a later request to be holding up. Not `busyCount === 0`, which is also true for
  // the moment before the first one is even sent.
  const busy = busyCount > 0;
  const [answered, setAnswered] = useState(false);
  const asked = useRef(false);
  useEffect(() => {
    if (busy) asked.current = true;
    else if (asked.current) setAnswered(true);
  }, [busy]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const word = useCallback((full: string, plain: string) => (simple ? plain : full), [simple]);

  // The server's names first — it is the only side that can see what a newly registered
  // system was called — then the two this codebase has always known, then the key itself.
  const systemLabel = useCallback(
    (key: string) => labelFor(key, me.system_labels),
    [me.system_labels],
  );

  const signOut = useCallback(async () => {
    await write('/logout');
    // Before the reload, or the sign-in page would be preceded by the frame of the
    // dashboard just left.
    rememberSession(false);
    window.location.replace('/login');
  }, []);

  const value = useMemo<AppValue>(
    () => ({
      me,
      days,
      setDays,
      system,
      setSystem,
      simple,
      word,
      systemLabel,
      theme,
      toggleTheme,
      refetching: busy && answered,
      track,
      nonce,
      reload,
      signOut,
    }),
    [
      me, days, system, simple, word, systemLabel, theme, toggleTheme,
      busy, answered, track, nonce, reload, signOut,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppValue {
  const value = useContext(AppContext);
  if (!value) throw new Error('useApp must be used inside <AppProvider>.');
  return value;
}
